import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  AllCommunityModule,
  ModuleRegistry,
  ICellRendererParams,
  IGetRowsParams,
  RowStyle,
} from 'ag-grid-community';
import AddGameModal from '../../components/modals/AddGameModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import TrialLimitModal from '../../components/modals/TrialLimitModal';
import { ScheduleUploadModal } from '../../components/upload';
import api from '../../config/axiosConfig';
import { API_URLS, BILLING_STATUS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { PermissionLevel } from '../../config/permissions';
import { formatLocalDate, getHoursSinceGameStart, getGameTimestamp } from '../../utils/dateUtils';
import { PermissionGuard } from '../../components/common/PermissionGuard';
import GameModeSwitcher from '../../components/GameModeSwitcher';
import { BillingInfo, getBillingStatus, isBillingEnabled } from '../../services/billingService';
import { GAME_MODE_PRACTICE, GAME_MODE_REGULAR, GameMode, getGameModeLabel, parseGameMode } from '../../utils/seasonType';
// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface GameData {
  _id?: string;
  name: string;
  location: string;
  type: string;
  date: string;
  time: string;
  timezone?: string;
  opponentId?: string;
  opponentName?: string;
  [key: string]: any;
}

const Games: React.FC = () => {
  const clientIdFromSession = sessionManager.getClientId();
  const isEmployee = sessionManager.isEmployee();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [view, setView] = useState('Upcoming');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isTrialLimitModalOpen, setIsTrialLimitModalOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<GameData | null>(null);
  const [editGame, setEditGame] = useState<GameData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSeason, setActiveSeason] = useState<any>(null);
  const canAccessPracticeGames = sessionManager.isEmployee() || sessionManager.canAccessPracticeGames();
  const [gamesMode, setGamesMode] = useState<GameMode>(() => {
    const stored = parseGameMode(sessionManager.getGamesMode());
    if (stored === GAME_MODE_PRACTICE && !(sessionManager.isEmployee() || sessionManager.canAccessPracticeGames())) {
      sessionManager.setGamesMode(GAME_MODE_REGULAR);
      return GAME_MODE_REGULAR;
    }
    return stored;
  });

  useEffect(() => {
    if (!canAccessPracticeGames && gamesMode === GAME_MODE_PRACTICE) {
      sessionManager.setGamesMode(GAME_MODE_REGULAR);
      setGamesMode(GAME_MODE_REGULAR);
    }
  }, [canAccessPracticeGames, gamesMode]);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const gridApi = useRef<any>(null);
  const canUploadSchedule =
    isEmployee ||
    !isBillingEnabled(billing) ||
    billing?.status === BILLING_STATUS.ACTIVE ||
    billing?.status === BILLING_STATUS.INVOICE_SENT;

  // Define the datasource using useMemo to prevent unnecessary recreations
  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;
        if (getRowsParams.startRow === 0) setIsLoading(true);

        try {
          const sessionSeasonId = sessionManager.getSeasonId();
          const params: any = {
            page: page + 1,
            limit: limit,
            flag: view.toLowerCase(),
            clientId: clientIdFromSession,
            gameMode: gamesMode,
          };
          if (sessionSeasonId) {
            params.seasonId = sessionSeasonId;
          }

          const response = await api.get(API_URLS.GET_GAMES, { params });
          if (response.data.success) {
            // Set season data from response on first load
            if (getRowsParams.startRow === 0) {
              if (response.data.season) {
                const season = response.data.season;
                const seasonIdStr = season._id?.toString?.() || season._id;
                setActiveSeason(season);

                // Align session to the active season actually used (drops stale inactive seasonIds)
                sessionManager.setSeasonId(seasonIdStr);
                sessionManager.setSeasonName(season.name);
                sessionManager.setStartDate(season.startDate);
                sessionManager.setEndDate(season.endDate);
              }
            }

            // Sort the data on the frontend by date and time (considering timezone)
            let sortedData = [...response.data.data];
            sortedData.sort((a, b) => {
              // Use getGameTimestamp for accurate timezone-aware comparison
              const timestampA = getGameTimestamp({
                date: a.date,
                time: a.time,
                timezone: a.timezone,
              });
              const timestampB = getGameTimestamp({
                date: b.date,
                time: b.time,
                timezone: b.timezone,
              });

              // For "Past" view, show most recent first (descending)
              // For "Upcoming" view, show nearest first (ascending)
              if (view === 'Past') {
                return timestampB - timestampA;
              } else {
                return timestampA - timestampB;
              }
            });

            getRowsParams.successCallback(sortedData, response.data.total);
            if (getRowsParams.startRow === 0) setIsLoading(false);
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch games');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch games.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize, view, clientIdFromSession, gamesMode],
  );

  const handleGamesModeChange = (mode: GameMode) => {
    if (mode === gamesMode) return;
    sessionManager.setGamesMode(mode);
    setGamesMode(mode);
    if (gridApi.current) {
      setIsLoading(true);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = () => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      // Update the datasource with new page size
      setIsLoading(true);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (game: GameData) => {
    setEditGame(game);
    setIsModalOpen(true);
  };

  // Delete button logic
  const handleDeleteClick = (game: GameData) => {
    setGameToDelete(game);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (gameToDelete && gameToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_GAME, {
          params: { _id: gameToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('Game deleted successfully!');
          if (gridApi.current) {
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to delete game');
        }
      } catch (error) {
        showErrorMsg('Failed to delete game.');
      }
    }
    setIsDeleteDialogOpen(false);
    setGameToDelete(null);
  };

  // Only refresh grid after add or update
  const handleGameAddedOrUpdated = async () => {
    setIsModalOpen(false);
    setEditGame(null);
    if (gridApi.current) {
      gridApi.current.purgeInfiniteCache();
    }

    if (!isEmployee) {
      const response = await getBillingStatus();
      if (response.success && response.billing) {
        setBilling(response.billing);
      }
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditGame(null);
  };

  // Handle view change
  const handleViewChange = (newView: string) => {
    setView(newView);
    if (gridApi.current) {
      setIsLoading(true);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Handle row click to navigate to stats page
  const handleRowClick = (event: any) => {
    const target = event.event?.target;
    if (target) {
      if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('.ag-cell[col-id="5"]')) {
        return;
      }
    }

    const gameData = event.data;
    if (gameData && gameData._id) {
      sessionManager.setGameId(gameData._id);
      // sessionManager.setGameName(gameData.name);
      sessionManager.setGameName(gameData.opponentName);

      const gameId = gameData._id;

      // Navigate to the new game details route
      navigate(`/games/${gameId}`);
    }
  };

  const NameCellRenderer: React.FC<ICellRendererParams<GameData>> = ({ value }) => {
    if (!value) return <span>-</span>;
    const name = String(value);
    return (
      <span className="block min-w-0 w-full truncate" title={name}>
        {name}
      </span>
    );
  };

  const DateCellRenderer: React.FC<ICellRendererParams<GameData>> = ({ value }) => {
    if (!value) return <span>-</span>;
    return <span>{formatLocalDate(value)}</span>;
  };

  const TimeCellRenderer: React.FC<ICellRendererParams<GameData>> = ({ value }) => {
    if (!value) return <span>-</span>;
    return <span>{value}</span>;
  };

  const TimezoneCellRenderer: React.FC<ICellRendererParams<GameData>> = ({ value }) => {
    if (!value) return <span>-</span>;

    // Convert timezone values to user-friendly display names
    const timezoneDisplayMap: { [key: string]: string } = {
      alaska: 'US/Alaska',
      central: 'US/Central',
      eastern: 'US/Eastern',
      hawaii: 'US/Hawaii',
      mountain: 'US/Mountain',
      pacific: 'US/Pacific',
    };

    return <span>{timezoneDisplayMap[value] || value}</span>;
  };

  const ActionsCellRenderer: React.FC<ICellRendererParams<GameData>> = ({ data }) => {
    const canEdit = sessionManager.canPerformAction('games.game', 'edit');
    const canDelete = sessionManager.canPerformAction('games.game', 'delete');

    // Check if user has unlock permission (statsLockAfterTimeout)
    const canUnlock = sessionManager.hasPermission('games.gameMenu.statsLockAfterTimeout', PermissionLevel.EDIT);

    // Check if game is locked (more than 24 hours ago from start time)
    // Must call hooks before any conditional returns
    const isGameLocked = React.useMemo(() => {
      if (!data || !data.date) return false;

      try {
        const hoursSinceGame = getHoursSinceGameStart({
          date: data.date,
          time: data.time,
          timezone: data.timezone,
        });
        return hoursSinceGame > 24;
      } catch (e) {
        console.error('Error parsing game date/time:', e);
        return false;
      }
    }, [data]);

    // Early return after all hooks
    if (!data) return null;

    // Hide buttons if game is locked and user cannot unlock
    const shouldHideButtons = isGameLocked && !canUnlock;

    if (shouldHideButtons) {
      return (
        <div className="flex items-center justify-center">
          <span
            className="text-gray-500"
            title="Game locked - stats are locked for games that started more than 24 hours ago"
          >
            🔒 Locked
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-2">
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(data);
            }}
            className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
            title={gamesMode === GAME_MODE_PRACTICE ? 'Edit / Rename Game' : 'Edit Game'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
              />
            </svg>
          </button>
        )}
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(data);
            }}
            className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
            title="Delete Game"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const isTournamentContext = Boolean(activeSeason?.isTournament);
  const isPracticeMode = gamesMode === GAME_MODE_PRACTICE;
  const columnDefs: ColDef<GameData>[] = [
    {
      field: isPracticeMode ? 'name' : 'opponentName',
      headerName: 'Name',
      sortable: true,
      flex: 2,
      minWidth: 110,
      wrapText: false,
      autoHeight: false,
      cellClass: 'games-name-cell',
      cellRenderer: NameCellRenderer,
      tooltipValueGetter: (params) =>
        isPracticeMode ? params.data?.name || params.data?.opponentName || 'Intra Squad' : params.data?.opponentName,
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      },
      valueGetter: (params) =>
        isPracticeMode ? params.data?.name || params.data?.opponentName || 'Intra Squad' : params.data?.opponentName,
    },
    ...(isTournamentContext
      ? []
      : [
          {
            field: 'location',
            headerName: 'Location',
            sortable: true,
            flex: 1,
            minWidth: 150,
          },
          {
            field: 'type',
            headerName: 'Type',
            sortable: true,
            flex: 1,
            minWidth: 120,
          },
        ]),
    {
      field: 'date',
      headerName: 'Date',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: DateCellRenderer,
    },
    {
      field: 'time',
      headerName: 'Time',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: TimeCellRenderer,
    },
    {
      field: 'timezone',
      headerName: 'Timezone',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: TimezoneCellRenderer,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 100,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 100,
      },
    },
  ];

  // Function to apply alternating row styles
  const getRowStyle = (params: any): RowStyle => {
    const baseStyle = { cursor: 'pointer' };
    if (params.node.rowIndex % 2 === 1) {
      return { ...baseStyle, backgroundColor: '#eafbe3' };
    }
    return baseStyle;
  };

  useEffect(() => {
    const loadBilling = async () => {
      if (isEmployee) {
        return;
      }

      const response = await getBillingStatus();
      if (response.success && response.billing) {
        setBilling(response.billing);
      }
    };

    loadBilling();
  }, [isEmployee]);

  const shouldBlockAddGameForTrialLimit = (billingInfo?: BillingInfo | null): boolean => {
    if (!billingInfo) return false;
    if (!isBillingEnabled(billingInfo)) return false;

    const status = billingInfo.status;
    const isTrialOrPending = status === BILLING_STATUS.TRIAL || status === BILLING_STATUS.PENDING;
    if (!isTrialOrPending) return false;

    return (billingInfo.freeGamesRemaining || 0) <= 0;
  };

  const handleAddGameClick = async () => {
    if (!clientIdFromSession) {
      showErrorMsg('Please select a Client before adding a game.');
      return;
    }

    if (!activeSeason) {
      showErrorMsg('No active season found. Please create an active season first.');
      return;
    }

    // Always refresh billing before deciding to open the form. The local value can
    // be stale right after the third trial game is added.
    let billingToUse = billing;
    if (!isEmployee) {
      const response = await getBillingStatus();
      if (response.success && response.billing) {
        billingToUse = response.billing;
        setBilling(response.billing);
      }
    }

    if (shouldBlockAddGameForTrialLimit(billingToUse)) {
      setIsTrialLimitModalOpen(true);
      return;
    }

    setEditGame(null);
    setIsModalOpen(true);
  };

  useEffect(() => {
    return () => {
      // Cleanup effect: close any open modals when the component unmounts or when navigating away
      setIsModalOpen(false);
      setEditGame(null);
    };
  }, []);

  const showModeSwitcher = Boolean(activeSeason && !activeSeason.isTournament && canAccessPracticeGames);

  return (
    <div className="w-full min-w-0 max-w-full p-4">
      <div className="mb-4 space-y-3">
        {showModeSwitcher && (
          <div className="flex justify-center">
            <GameModeSwitcher mode={gamesMode} onChange={handleGamesModeChange} />
          </div>
        )}
        <div className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] items-center gap-x-2 gap-y-2 md:grid-cols-3 md:gap-3">
          <h1 className="col-span-2 min-w-0 truncate text-center text-xl font-bold xss:text-2xl md:col-span-1 md:text-left">
            Games{' '}
            {activeSeason && (
              <span className="text-lg text-gray-600">
                - {activeSeason.name}
                {showModeSwitcher ? ` (${getGameModeLabel(gamesMode)})` : ''}
              </span>
            )}
          </h1>
          <div className="flex items-center justify-start md:justify-center">
            <button
              onClick={() => handleViewChange('Upcoming')}
              className={`inline-flex h-9 items-center rounded px-3 text-sm font-semibold ${
                view === 'Upcoming'
                  ? isPracticeMode
                    ? 'bg-amber-400 text-amber-950'
                    : 'bg-[#4eea4d] text-black'
                  : 'text-gray-600'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => handleViewChange('Past')}
              className={`inline-flex h-9 items-center rounded px-3 text-sm font-semibold ${
                view === 'Past'
                  ? isPracticeMode
                    ? 'bg-amber-400 text-amber-950'
                    : 'bg-[#4eea4d] text-black'
                  : 'text-gray-600'
              }`}
            >
              Past
            </button>
          </div>
          <div className="flex items-center justify-end space-x-2 md:space-x-3">
            {canUploadSchedule && (
              <PermissionGuard resource="games.uploadSchedule" action="add">
                <button
                  type="button"
                  title="Upload Schedule"
                  aria-label="Upload Schedule"
                  onClick={() => {
                    if (!clientIdFromSession) {
                      showErrorMsg('Please select a Client before uploading schedule.');
                      return;
                    }
                    if (!activeSeason) {
                      showErrorMsg('No active season found. Please create an active season first.');
                      return;
                    }
                    setIsUploadModalOpen(true);
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded bg-green-600 text-white shadow-2xl transition-colors hover:bg-green-700 md:w-auto md:space-x-2 md:px-4 md:font-semibold"
                >
                  <svg className="h-5 w-5 md:h-4 md:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <span className="hidden md:inline">Upload Schedule</span>
                </button>
              </PermissionGuard>
            )}
            <PermissionGuard resource="games.game" action="add">
              <button
                type="button"
                title="Add Game"
                aria-label="Add Game"
                onClick={handleAddGameClick}
                className={`${
                  isPracticeMode
                    ? 'bg-amber-400 text-amber-950 hover:bg-amber-500'
                    : 'bg-[#4eea4d] text-black hover:bg-[#41ca41]'
                } flex h-9 w-9 items-center justify-center rounded shadow-2xl transition-colors md:w-auto md:px-4 md:font-semibold`}
              >
                <svg className="h-5 w-5 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden md:inline">Add Game</span>
              </button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      <div className="ag-theme-alpine w-full min-w-0 overflow-x-auto" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          loading={isLoading}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No games found</span>'}
          getRowStyle={getRowStyle}
          onRowClicked={handleRowClick}
        />
      </div>

      {isModalOpen && (
        <AddGameModal
          onClose={handleModalClose}
          onGameAdded={handleGameAddedOrUpdated}
          editGame={editGame}
          isTournamentMode={Boolean(activeSeason?.isTournament)}
          isPracticeMode={isPracticeMode}
        />
      )}

      <TrialLimitModal
        isOpen={isTrialLimitModalOpen}
        onClose={() => setIsTrialLimitModalOpen(false)}
        onSubscribe={() => {
          setIsTrialLimitModalOpen(false);
          navigate('/billing');
        }}
      />

      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Game"
          message={`Are you sure you want to delete ${gameToDelete?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}

      {isUploadModalOpen && (
        <ScheduleUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadComplete={() => {
            // Refresh the grid data
            if (gridApi.current) {
              gridApi.current.refreshInfiniteCache();
            }
          }}
        />
      )}
    </div>
  );
};

export default Games;
